import { Button, IconButton, Stack } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import {
  ComponentSelector,
  Icon,
  VailAccordion,
  VailSelect,
  VailTextField,
} from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType } from '@/utils/api'

import { DataIntakeSettings } from '../types'

export type DataIntakeForm = {
  settings: DataIntakeSettings
  name: string
  description: string
  type: ComponentType.dataIntake
}
export const Settings = () => {
  const form = useForm<DataIntakeForm>({
    defaultValues: { type: ComponentType.dataIntake },
  })
  const customRecipesArray = useFieldArray({
    control: form.control,
    name: 'settings.customRecipes',
  })
  const navigate = useNavigate()
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()

  const create = useCreateCustomComponent<DataIntakeSettings>(orgID)
  const current = useGetCustomComponent<DataIntakeSettings>(componentID)
  const update = useUpdateCustomComponent<DataIntakeSettings>(componentID)
  const { reset } = form

  useEffect(() => {
    if (!current.data) return
    const { name, description, settings } = current.data
    reset({ description, name, settings })
  }, [current.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    if (componentID)
      return await update.mutateAsync({
        ...data,
        id: componentID,
        type: ComponentType.dataIntake,
      })
    const res = await create.mutateAsync({
      ...data,
      type: ComponentType.dataIntake,
    })
    return navigate(`../${res?.id}`)
  })
  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        rowGap={2}
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save data intake')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              control={form.control}
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              control={form.control}
              defaultValue={ComponentType.dataIntake}
              label="Type"
              name="type"
              options={[
                {
                  label: 'Data Intake',
                  value: ComponentType.dataIntake,
                },
              ]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              control={form.control}
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
          title="Custom Recipes"
        >
          {customRecipesArray.fields.map((field, idx) => (
            <Stack
              key={field.id}
              alignItems="center"
              columnGap={3}
              direction="row"
              maxWidth={500}
            >
              <IconButton
                sx={{ height: 20, width: 20 }}
                onClick={() => customRecipesArray.remove(idx)}
              >
                <Icon name="delete" />
              </IconButton>
              <ComponentSelector
                control={form.control}
                name={`settings.customRecipes.${idx}`}
                type={ComponentType.recipe}
              />
            </Stack>
          ))}
          <Button
            sx={{ alignSelf: 'flex-end' }}
            onClick={() =>
              customRecipesArray.append({
                componentName: '',
                name: '',
                type: ComponentType.recipe,
                version: '',
              })
            }
          >
            Add Custom Recipe
          </Button>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
